import { useEffect, useState, useCallback } from 'react'
import { api } from '../lib/api'
import { formatCurrency } from '../lib/format'
import type { Deal, PaginatedResult } from '@shared/types'
import { TableSkeleton } from '../components/ui/Skeletons'
import { EmptyState } from '../components/ui/EmptyState'
import { StatusBadge } from '../components/ui/StatusBadge'
import { GlowCard } from '../components/ui/GlowCard'
import { Drawer } from '../components/ui/Drawer'
import { useClientOptions } from '../hooks/useEntityOptions'
import { Button } from '../components/ui/Button'

const PlusIcon = <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>

const STAGES = ['lead', 'qualified', 'proposal', 'negotiation', 'won', 'lost'] as const

export default function Deals() {
  const [deals, setDeals] = useState<Deal[]>([])
  const [loading, setLoading] = useState(true)
  const [stage, setStage] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [formData, setFormData] = useState({ client_slug: '', name: '', value: 0, stage: 'lead' as string })
  const [saving, setSaving] = useState(false)
  const { options: clientOptions } = useClientOptions()

  const loadDeals = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams({ page: String(page), limit: '20' })
      if (stage) params.set('stage', stage)
      const result = await api.get<PaginatedResult<Deal>>(`/deals?${params}`)
      setDeals(result.data)
      setTotalPages(result.totalPages)
    } catch { /* handled */ }
    finally { setLoading(false) }
  }, [page, stage])

  useEffect(() => { loadDeals() }, [loadDeals])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    try {
      await api.post('/deals', formData)
      setDrawerOpen(false)
      setFormData({ client_slug: '', name: '', value: 0, stage: 'lead' })
      loadDeals()
    } catch { /* error shown */ }
    finally { setSaving(false) }
  }

  return (
    <div className="page-enter">
      <div className="page-header">
        <h1 className="page-title">Deals</h1>
        <Button icon={PlusIcon} onClick={() => setDrawerOpen(true)}>New Deal</Button>
      </div>

      <div className="mb-4">
        <div className="v3-select-wrap max-w-[160px]">
          <select value={stage} onChange={(e) => { setStage(e.target.value); setPage(1) }} className="v3-select">
            <option value="">All Stages</option>
            {STAGES.map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
          </select>
          <span className="v3-select-chevron">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6,9 12,15 18,9" />
            </svg>
          </span>
        </div>
      </div>

      {loading ? (
        <TableSkeleton />
      ) : deals.length === 0 ? (
        <EmptyState
          title="Pipeline is empty — add your first deal"
          description="Track your sales pipeline here."
          action={<Button onClick={() => setDrawerOpen(true)}>New Deal</Button>}
        />
      ) : (
        <>
          <GlowCard style={{ padding: 0 }} className="hidden sm:block">
            <table className="v3-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Stage</th>
                  <th className="text-right">Value</th>
                  <th className="text-right">Probability</th>
                  <th>Expected Close</th>
                </tr>
              </thead>
              <tbody>
                {deals.map((d) => (
                  <tr key={d.id}>
                    <td style={{ fontWeight: 500, color: 'var(--text)', fontSize: 14 }}>{d.name}</td>
                    <td><StatusBadge status={d.stage} /></td>
                    <td className="text-right mono" style={{ fontFamily: 'var(--font-mono)', fontWeight: 500, fontSize: 14 }}>{formatCurrency(d.value)}</td>
                    <td className="text-right" style={{ color: 'var(--text-secondary)', fontSize: 14, fontFamily: 'var(--font-mono)' }}>{d.probability}%</td>
                    <td style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{d.expected_close ? new Date(d.expected_close).toLocaleDateString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </GlowCard>

          <div className="sm:hidden space-y-3">
            {deals.map((d) => (
              <div key={d.id} className="mobile-card">
                <div className="flex justify-between items-start">
                  <p style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)' }}>{d.name}</p>
                  <StatusBadge status={d.stage} />
                </div>
                <div className="mobile-card-row mt-1">
                  <span className="mono" style={{ fontSize: 14, fontWeight: 500, color: 'var(--accent-text)', fontFamily: 'var(--font-mono)' }}>{formatCurrency(d.value)}</span>
                  <span style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)' }}>{d.probability}% probability</span>
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Previous</Button>
              <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{page} / {totalPages}</span>
              <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next</Button>
            </div>
          )}
        </>
      )}

      <Drawer isOpen={drawerOpen} onClose={() => setDrawerOpen(false)} title="New Deal">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="v3-field-label">Client *</label>
            <div className="v3-select-wrap">
              <select value={formData.client_slug} onChange={(e) => setFormData({ ...formData, client_slug: e.target.value })} className="v3-select" required>
                <option value="">Select client...</option>
                {clientOptions.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
              <span className="v3-select-chevron">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6,9 12,15 18,9" />
                </svg>
              </span>
            </div>
          </div>
          <div>
            <label className="v3-field-label">Deal Name *</label>
            <input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="v3-input" required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="v3-field-label">Value ($)</label>
              <input type="number" step="0.01" value={formData.value} onChange={(e) => setFormData({ ...formData, value: parseFloat(e.target.value) || 0 })} className="v3-input" />
            </div>
            <div>
              <label className="v3-field-label">Stage</label>
              <div className="v3-select-wrap">
                <select value={formData.stage} onChange={(e) => setFormData({ ...formData, stage: e.target.value })} className="v3-select">
                  {STAGES.map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
                </select>
                <span className="v3-select-chevron">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6,9 12,15 18,9" />
                  </svg>
                </span>
              </div>
            </div>
          </div>
          <Button type="submit" loading={saving} style={{ width: '100%' }}>
            {saving ? 'Creating...' : 'Create Deal'}
          </Button>
        </form>
      </Drawer>
    </div>
  )
}
